import { 
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useEffect,
} from "react";

import { extend, useTick } from "@pixi/react";
import type { Graphics as GraphicsType, Text as TextType, PointData } from "pixi.js";
import { Graphics, Texture } from "pixi.js";

interface PixBimMeasureProps {
  scale?: number,
  angle?: number,
  points: PointData[],
  strokeWidth?: number,
  strokeColor?: string
}

export interface PixBimMeasureType {
  updateMeasure: (point: PointData) => void;
}

extend({ Text });
extend({ Graphics });

const PixBimMeasure = forwardRef<PixBimMeasureType, PixBimMeasureProps>(
  ({ points, strokeWidth = 10, strokeColor = "#666666", scale = 100, angle = 0 }, ref) => {

    const textRef = useRef<TextType>(null);
    const endPointRef = useRef<PointData>({x: 0, y: 0});
    const graphicsRef = useRef<Graphics | null>(null);
    useImperativeHandle(ref, () => {
      return {
        updateMeasure: (point: PointData) => {
          endPointRef.current = point
        },
      };
    });
    useEffect(() => {
      const [first, last] = points;
      if (last) {
        endPointRef.current = last
      } else if (first) {
        endPointRef.current = first;
      }
    }, [points])

    useTick(() => {
      const g = graphicsRef.current;
      if (g) {
        go(g);
      }
    });
    const go = (g: GraphicsType) => {
      g.clear();
      const texture: Texture = Texture.WHITE;
      texture.source.scaleMode = "nearest";
      const [first] = points;
      const {x: endX, y: endY} = endPointRef.current || first.x;
      const dx = endX - first.x;
      const dy = endY - first.y;
      const dist = Math.sqrt(dx * dx + dy * dy) / scale;
      const rounded = Math.round(dist * 100) / 100;
      const middle = {
        x: ((first.x - 40) + endX) / 2,
        y: ((first.y - 40) + endY) / 2
      }
      if (textRef.current) {
        textRef.current.position.x = middle.x;
        textRef.current.position.y = middle.y;
        textRef.current.text = `${rounded} m`;
      }
      g.moveTo(first.x, first.y);
      g.lineTo(endX, endY);
      g.stroke({ width: 100, color: 0x4FC3F7, alpha: 0.6 });
    }

    const draw = useCallback(
      (g: GraphicsType) => {
        g.clear();
        const texture: Texture = Texture.WHITE;
        texture.source.scaleMode = "nearest";
        const [first, last] = points;
        g.moveTo(first.x, first.y);
        if (last) {
          g.lineTo(last.x, last.y);
        } else {
          g.lineTo(first.x, first.y);
        }
        g.stroke({ width: 100, color: 0x4FC3F7, alpha: 0.6 });
      },
      [points]
    );


    return <>
      <graphics 
        label="Measure"
        ref={graphicsRef}
        draw={draw}
      />
      <pixiText
        ref={textRef}
        position={points[0]}
        angle={-angle}
        style={{ 
          fontSize: 80,
          fontWeight: 'bold',
          align: "center", 
          fill: '#000000'
        }}
        zIndex={1}
      />
    </>
  });

export default PixBimMeasure;
